import {useCallback} from 'react';

import {useApolloClient, useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    EditContactBusinessCardField as EditContactBusinessCardFieldMutation,
    EditContactBusinessCardFieldVariables,
    GetPeepBusinessCardInfo as GetPeepBusinessCardInfoQuery,
    GetPeepBusinessCardInfoVariables,
    EavPatchInput,
} from 'nutshell-graphql-types';

import {getOptimisticEavPatchResponse} from '../helpers/get-optimistic-peep-for-patch';
import EDIT_CONTACT_BUSINESS_CARD_FIELD from '../mutations/edit-contact-business-card-field.graphql';
import GET_PEEP_BUSINESS_CARD_INFO from '../queries/get-peep-business-card-info.graphql';

export function useEditContactBusinessCardField(): {
    editContactBusinessCardField: (input: EavPatchInput) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} {
    const client = useApolloClient();
    const [editContactBusinessCardFieldMutation, {loading, error}] = useMutation<
        EditContactBusinessCardFieldMutation,
        EditContactBusinessCardFieldVariables
    >(EDIT_CONTACT_BUSINESS_CARD_FIELD);

    const editContactBusinessCardField = useCallback(
        (input: EavPatchInput) => {
            const cached = client.readQuery<
                GetPeepBusinessCardInfoQuery,
                GetPeepBusinessCardInfoVariables
            >({
                query: GET_PEEP_BUSINESS_CARD_INFO,
                variables: {
                    entityId: input.entityId,
                },
            });

            if (!cached?.peep || typeof input.name !== 'string') {
                return editContactBusinessCardFieldMutation({variables: {input}});
            }

            const optimisticResponse = getOptimisticEavPatchResponse(
                cached.peep,
                input.attributeValueId,
                input.name,
                'contact'
            );

            return editContactBusinessCardFieldMutation({
                variables: {input},
                optimisticResponse,
            });
        },
        [client, editContactBusinessCardFieldMutation]
    );

    return {
        editContactBusinessCardField,
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
}
